import { ref } from "vue";
import type { ConfirmEsignRequest } from "@/models/api";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { $api } from "@/services/api";
import useLoanConfigurationHelpers from "@/use/loanConfigurationModuleHelpers";

export default function useEsignConfirmation() {
  const store = useStore();
  const router = useRouter();

  const esignResponseError = ref<boolean>(false);
  const esignErrorMessage = ref<string>("");
  const gettingData = ref<boolean>(true);
  const loading = ref<boolean>(false);

  // Store a reference to the opened window
  const openedWindow = ref<Window | null>(null);

  const { regenerateLegalPacketCheck } = useLoanConfigurationHelpers();

  // Close the tab you opened
  function closeLoadingTab() {
    if (openedWindow.value) {
      openedWindow.value.close();
      openedWindow.value = null; // Clear the reference
    }
  }

  function openLoadingTab() {
    // 1. Resolve the route object to find its internal properties
    const routeData = router.resolve({
      name: "generatingDocuments",
    });

    // 2. Pass the resolved href string into window.open
    openedWindow.value = window.open(routeData.href, "_blank");
  }

  async function confirmChangesREFI() {
    loading.value = true;
    esignResponseError.value = false;

    // Open window synchronously before await — required for iOS Safari popup policy
    openLoadingTab();

    const confirmEsignRequest = {
      applicationId: store.state.appData.application.id,
      applicantId: store?.state?.appData?.currentApplicant?.id,
    } as ConfirmEsignRequest;

    const esignSigningUrl = await $api.refi.confirmEsign(confirmEsignRequest);

    if (esignSigningUrl?.error) {
      closeLoadingTab();
      esignResponseError.value = true;
      esignErrorMessage.value = esignSigningUrl.title;
      loading.value = false;
      await store.dispatch("consentsModule/getConsentsForAllApplicants");
    } else {
      store.dispatch("applicationInfoModule/setCanEdit", false);

      if (openedWindow.value && esignSigningUrl.data) {
        openedWindow.value.location.href = esignSigningUrl.data || "";
      }
      await store.dispatch("checklistModule/getSortedChecklistItems");
      await store.dispatch("consentsModule/getConsentsForAllApplicants");

      await store.dispatch(
        "applicationInfoModule/getRefiDetails",
        store.state?.appData?.application?.id,
      );
      store.dispatch("applicationInfoModule/closeModal", "ConfirmYourRefinanceDetails");
      loading.value = false;
    }

    console.log("esignSigningUrl", esignSigningUrl);
  }

  async function confirmChanges() {
    loading.value = true;
    esignResponseError.value = false;

    // Open window synchronously before await — required for iOS Safari popup policy
    openLoadingTab();

    const configurationChanged = regenerateLegalPacketCheck();

    const confirmEsignRequest = {
      applicationId: store.state.appData.application.id,
      applicantId: store?.state?.appData?.currentApplicant?.id,
      regenerateLegalPacket: configurationChanged,
    } as ConfirmEsignRequest;

    const esignSigningUrl = await $api.pel.confirmEsign(confirmEsignRequest);

    if (esignSigningUrl?.error) {
      closeLoadingTab();
      esignResponseError.value = true;
      esignErrorMessage.value = esignSigningUrl.title;

      loading.value = false;
      await store.dispatch("consentsModule/getConsentsForAllApplicants");
    } else {
      store.dispatch("loanConfigurationModule/setEditable", false);

      if (openedWindow.value && esignSigningUrl.data) {
        openedWindow.value.location.href = esignSigningUrl.data || "";
      }
      await store.dispatch("checklistModule/getSortedChecklistItems");
      await store.dispatch("consentsModule/getConsentsForAllApplicants");

      await store.dispatch(
        "loanConfigurationModule/getPelDetails",
        store.state.appData?.application?.id,
      );
      store.dispatch("applicationInfoModule/closeModal", "ConfirmYourLoanDetails");

      loading.value = false;
    }

    console.log("esignSigningUrl", esignSigningUrl);
  }

  return {
    confirmChanges,
    confirmChangesREFI,
    esignErrorMessage,
    esignResponseError,
    gettingData,
    loading,
  };
}
